import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import CampaignPreview from "@/components/CampaignPreview";
import {
  Megaphone,
  Users,
  Workflow,
  Sparkles,
  Trash2,
  Eye,
  Pencil,
  Plus,
  Send,
  MessageSquare,
  Rocket,
  Check,
  X as XIcon,
} from "lucide-react";

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700",
  scheduled: "bg-amber-50 text-amber-700",
  active: "bg-emerald-50 text-emerald-700",
  paused: "bg-slate-200 text-slate-700",
  completed: "bg-blue-50 text-blue-700",
  archived: "bg-slate-100 text-slate-500",
};

const STATUS_OPTIONS = ["draft", "scheduled", "active", "paused", "completed", "archived"];
const CATEGORY_OPTIONS = [
  "sleep",
  "metabolic",
  "wellness",
  "longevity",
  "diagnostics",
  "reactivation",
  "subscription",
  "upsell",
  "acquisition",
];

const TABS = ["overview", "campaigns", "templates"];

export default function AdminCampaignManager() {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [previewClinicLabel, setPreviewClinicLabel] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [an, cs, tp, cl] = await Promise.all([
        api.get("/admin/campaigns/analytics"),
        api.get("/admin/campaigns"),
        api.get("/admin/campaigns/templates"),
        api.get("/admin/clinics-list"),
      ]);
      setAnalytics(an.data);
      setCampaigns(cs.data?.campaigns || []);
      setTemplates(tp.data?.templates || []);
      setClinics(cl.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const clinicLabelMap = useMemo(() => {
    const m = {};
    for (const c of clinics) {
      m[c.id] =
        c.clinic_name || c.organization || c.name || c.email || c.id?.slice(0, 8);
    }
    return m;
  }, [clinics]);

  const removeTemplate = async (tid) => {
    if (!window.confirm("Delete this template? Existing campaigns will not be affected.")) return;
    try {
      await api.delete(`/admin/campaigns/templates/${tid}`);
      toast.success("Template deleted.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPreview = async (cid) => {
    try {
      const r = await api.get(`/admin/campaigns/${cid}/preview`);
      setPreviewCampaign(r.data?.campaign || null);
      setPreviewClinicLabel(r.data?.clinic?.label || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEdit = async (cid) => {
    try {
      const r = await api.get(`/admin/campaigns/${cid}`);
      setEditing(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeCampaign = async (cid) => {
    if (!window.confirm("Delete this campaign? This action cannot be undone.")) return;
    try {
      await api.delete(`/admin/campaigns/${cid}`);
      toast.success("Campaign deleted.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Marketing"
      title="Campaign Manager"
      intro="Cross-clinic oversight of marketing campaigns, audiences, and library templates."
    >
      <div className="border-b border-slate-200 mb-5 -mx-2 px-2 overflow-x-auto">
        <div className="flex items-center gap-1" data-testid="admin-campaigns-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`admin-campaigns-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-[2px] capitalize ${
                tab === t
                  ? "text-slate-900 border-slate-900"
                  : "text-slate-500 hover:text-slate-900 border-transparent"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && tab === "overview" && analytics && (
        <div className="space-y-5" data-testid="admin-campaigns-overview">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Total campaigns" value={analytics.total_campaigns} icon={Megaphone} accent="bg-blue-500" />
            <Kpi label="Active campaigns" value={analytics.active_campaigns} icon={Sparkles} accent="bg-emerald-500" />
            <Kpi label="Audiences" value={analytics.audiences} icon={Users} accent="bg-violet-500" />
            <Kpi label="Automations" value={analytics.automations} icon={Workflow} accent="bg-amber-500" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Engagement totals (all clinics)</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {Object.entries(analytics.totals || {}).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 capitalize">{k}</div>
                  <div className="font-bold text-slate-900 text-xl">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && tab === "campaigns" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              All clinic campaigns — admins can preview, edit metadata, or remove on a clinic's behalf.
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="admin-campaign-create-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5" /> New campaign
            </button>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="admin-campaigns-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Campaign</th>
                  <th className="text-left px-4 py-3">Clinic</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`admin-campaign-row-${c.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {clinicLabelMap[c.clinic_user_id] || `${c.clinic_user_id?.slice(0, 8)}…`}
                    </td>
                    <td className="px-4 py-3 capitalize">{c.category}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[c.status] || "bg-slate-100"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openPreview(c.id)}
                          data-testid={`admin-campaign-preview-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 text-slate-700 hover:bg-slate-100"
                          title="Preview"
                        >
                          <Eye className="w-3.5 h-3.5" /> Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(c.id)}
                          data-testid={`admin-campaign-edit-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 text-blue-700 hover:bg-blue-50"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCampaign(c.id)}
                          data-testid={`admin-campaign-delete-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 text-rose-600 hover:bg-rose-50"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-500">No campaigns yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && tab === "templates" && (
        <div className="space-y-4" data-testid="admin-campaigns-templates">
          <div className="text-xs text-slate-500">
            Library templates available to all clinics. Adding/editing templates here updates the
            clinic-side library immediately.
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col" data-testid={`admin-template-${t.id}`}>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-blue-600 capitalize">{t.category}</div>
                <h4 className="mt-1 font-display font-bold text-slate-900">{t.name}</h4>
                <p className="mt-1 text-xs text-slate-600 line-clamp-2">{t.goal}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(t.channels || []).map((ch) => (
                    <span key={ch} className="text-[10px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 capitalize">{ch.replace("_", " ")}</span>
                  ))}
                </div>
                <div className="mt-auto pt-3 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => removeTemplate(t.id)}
                    className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 text-rose-600 hover:bg-rose-50"
                    data-testid={`admin-template-delete-${t.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <div className="sm:col-span-3 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
                No templates seeded.
              </div>
            )}
          </div>
        </div>
      )}

      {previewCampaign && (
        <div data-testid="admin-campaign-preview-modal">
          <PreviewActionBar
            campaign={previewCampaign}
            clinicLabel={previewClinicLabel}
          />
          <CampaignPreview
            campaign={previewCampaign}
            onClose={() => {
              setPreviewCampaign(null);
              setPreviewClinicLabel("");
            }}
          />
        </div>
      )}

      {editing && (
        <EditCampaignDialog
          campaign={editing}
          clinicLabel={clinicLabelMap[editing.clinic_user_id] || editing.clinic_user_id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {creating && (
        <CreateCampaignDialog
          clinics={clinics}
          templates={templates}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setTab("campaigns");
            refresh();
          }}
        />
      )}
    </ToolPageShell>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-xl ${accent}/10 bg-opacity-10 flex items-center justify-center`}>
        <Icon className={`w-4 h-4`} />
      </span>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function ModalShell({ title, eyebrow, onClose, children, footer, testId }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl my-10 mx-4 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                {eyebrow}
              </div>
            )}
            <div className="font-display font-bold text-slate-900 truncate">{title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full w-8 h-8 inline-flex items-center justify-center text-slate-500 hover:bg-slate-100"
            data-testid={`${testId}-close`}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">{children}</div>
        {footer && (
          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function EditCampaignDialog({ campaign, clinicLabel, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: campaign.name || "",
    goal: campaign.goal || "",
    category: campaign.category || "wellness",
    status: campaign.status || "draft",
    schedule_at: campaign.schedule_at || "",
    notes: campaign.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        goal: form.goal,
        category: form.category,
        status: form.status,
        notes: form.notes,
        schedule_at: form.schedule_at || null,
      };
      await api.patch(`/admin/campaigns/${campaign.id}`, payload);
      toast.success("Campaign updated.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      eyebrow={`Editing for ${clinicLabel}`}
      title={campaign.name || "Edit campaign"}
      onClose={onClose}
      testId="admin-campaign-edit-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="admin-campaign-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name || !form.goal}
            data-testid="admin-campaign-edit-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <Field label="Name">
        <input
          type="text"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          data-testid="admin-campaign-edit-name"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Goal">
        <input
          type="text"
          value={form.goal}
          onChange={(e) => set("goal", e.target.value)}
          data-testid="admin-campaign-edit-goal"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Category">
          <select
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            data-testid="admin-campaign-edit-category"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c} className="capitalize">{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="admin-campaign-edit-status"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize"
          >
            {STATUS_OPTIONS.map((c) => (
              <option key={c} value={c} className="capitalize">{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Schedule at (optional ISO datetime)">
        <input
          type="text"
          value={form.schedule_at || ""}
          onChange={(e) => set("schedule_at", e.target.value)}
          placeholder="2026-03-15T10:00:00Z"
          data-testid="admin-campaign-edit-schedule"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
        />
      </Field>
      <Field label="Notes">
        <textarea
          value={form.notes || ""}
          onChange={(e) => set("notes", e.target.value)}
          rows={3}
          data-testid="admin-campaign-edit-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="text-[11px] text-slate-500">
        Channel/landing-page content can be edited from the clinic's own builder. Admin edits here
        cover metadata + lifecycle status.
      </div>
    </ModalShell>
  );
}

function CreateCampaignDialog({ clinics, templates, onClose, onCreated }) {
  const [form, setForm] = useState({
    clinic_user_id: clinics[0]?.id || "",
    template_id: templates[0]?.id || "",
    name: "",
    goal: "",
    category: "wellness",
    use_template: true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.clinic_user_id) {
      toast.error("Select a clinic.");
      return;
    }
    setSaving(true);
    try {
      const payload = form.use_template
        ? { clinic_user_id: form.clinic_user_id, template_id: form.template_id, name: form.name || undefined }
        : {
            clinic_user_id: form.clinic_user_id,
            name: form.name,
            goal: form.goal,
            category: form.category,
          };
      if (!form.use_template && (!form.name || !form.goal)) {
        toast.error("Name and goal are required.");
        setSaving(false);
        return;
      }
      await api.post("/admin/campaigns", payload);
      toast.success("Campaign created.");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      eyebrow="Admin · Create campaign"
      title="New cross-clinic campaign"
      onClose={onClose}
      testId="admin-campaign-create-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="admin-campaign-create-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !form.clinic_user_id}
            data-testid="admin-campaign-create-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create campaign"}
          </button>
        </>
      }
    >
      <Field label="Clinic">
        <select
          value={form.clinic_user_id}
          onChange={(e) => set("clinic_user_id", e.target.value)}
          data-testid="admin-campaign-create-clinic"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">— Select a clinic —</option>
          {clinics.map((c) => (
            <option key={c.id} value={c.id}>
              {c.clinic_name || c.organization || c.name || c.email}
            </option>
          ))}
        </select>
      </Field>

      <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 w-fit">
        <button
          type="button"
          onClick={() => set("use_template", true)}
          data-testid="admin-campaign-create-mode-template"
          className={`text-xs font-semibold rounded-full px-3 py-1 ${
            form.use_template ? "bg-white shadow text-slate-900" : "text-slate-500"
          }`}
        >
          From template
        </button>
        <button
          type="button"
          onClick={() => set("use_template", false)}
          data-testid="admin-campaign-create-mode-scratch"
          className={`text-xs font-semibold rounded-full px-3 py-1 ${
            !form.use_template ? "bg-white shadow text-slate-900" : "text-slate-500"
          }`}
        >
          From scratch
        </button>
      </div>

      {form.use_template ? (
        <>
          <Field label="Template">
            <select
              value={form.template_id}
              onChange={(e) => set("template_id", e.target.value)}
              data-testid="admin-campaign-create-template"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.category}] {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Override name (optional)">
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Leave blank to keep template name + (admin copy) suffix"
              data-testid="admin-campaign-create-name"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="admin-campaign-create-name"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Goal">
            <input
              type="text"
              value={form.goal}
              onChange={(e) => set("goal", e.target.value)}
              data-testid="admin-campaign-create-goal"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Category">
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              data-testid="admin-campaign-create-category"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c} className="capitalize">{c}</option>
              ))}
            </select>
          </Field>
          <div className="text-[11px] text-slate-500">
            From-scratch campaigns start as drafts with no channels — the clinic can configure email,
            SMS, and landing-page content from their own builder.
          </div>
        </>
      )}
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function PreviewActionBar({ campaign, clinicLabel }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [smsRecipient, setSmsRecipient] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  // Preflight state
  const [pfEmail, setPfEmail] = useState("");
  const [pfPhone, setPfPhone] = useState("");
  const [pfSave, setPfSave] = useState(true);
  const [pfRunning, setPfRunning] = useState(false);
  const [pfResult, setPfResult] = useState(null);

  const hasSmsChannel = (campaign.channels || []).some(
    (c) => c.channel === "sms" && c.enabled !== false && (c.body_text || "").trim()
  );

  // Lazy-load saved test contacts the first time the preflight popover opens.
  useEffect(() => {
    if (!preflightOpen || pfEmail || pfPhone) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/preflight-contacts");
        if (cancelled) return;
        setPfEmail(r.data?.test_email || "");
        setPfPhone(r.data?.test_phone || "");
      } catch (e) {
        // Non-fatal — fall back to empty inputs.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preflightOpen, pfEmail, pfPhone]);

  const closeAll = () => {
    setEmailOpen(false);
    setSmsOpen(false);
    setPreflightOpen(false);
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const r = await api.post(`/admin/campaigns/${campaign.id}/send-test-email`, {
        to: emailRecipient.trim() || undefined,
      });
      toast.success(`Test email sent to ${r.data?.to || "your inbox"}.`);
      setEmailOpen(false);
      setEmailRecipient("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingEmail(false);
    }
  };

  const sendSms = async () => {
    if (!smsRecipient.trim()) {
      toast.error("Recipient phone is required.");
      return;
    }
    setSendingSms(true);
    try {
      const r = await api.post(`/admin/campaigns/${campaign.id}/send-test-sms`, {
        to: smsRecipient.trim(),
      });
      toast.success(`Test SMS sent to ${r.data?.to}.`);
      setSmsOpen(false);
      setSmsRecipient("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingSms(false);
    }
  };

  const runPreflight = async () => {
    setPfRunning(true);
    setPfResult(null);
    try {
      const r = await api.post(`/admin/campaigns/${campaign.id}/preflight`, {
        email: pfEmail.trim() || undefined,
        phone: pfPhone.trim() || undefined,
        save_defaults: pfSave,
      });
      setPfResult(r.data);
      if (r.data?.overall_ok) {
        toast.success("All channels passed pre-flight.");
      } else {
        toast.error("Pre-flight found issues — see results below.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPfRunning(false);
    }
  };

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[70] flex flex-col items-center gap-2 pointer-events-none"
      data-testid="admin-campaign-preview-actionbar"
    >
      <div className="flex items-center gap-2 pointer-events-auto">
        {clinicLabel && (
          <div className="rounded-full bg-slate-900 text-white text-[11px] font-semibold px-3 py-1 shadow-lg">
            Previewing for {clinicLabel}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            closeAll();
            setPreflightOpen(true);
          }}
          data-testid="admin-campaign-preflight-toggle"
          className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold px-3 py-1 shadow-lg hover:bg-blue-700"
        >
          <Rocket className="w-3 h-3" /> Pre-flight check
        </button>
        <button
          type="button"
          onClick={() => {
            closeAll();
            setEmailOpen(true);
          }}
          data-testid="admin-campaign-test-email-toggle"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-semibold px-3 py-1 shadow-lg hover:bg-emerald-700"
        >
          <Send className="w-3 h-3" /> Send test email
        </button>
        <button
          type="button"
          disabled={!hasSmsChannel}
          onClick={() => {
            closeAll();
            setSmsOpen(true);
          }}
          data-testid="admin-campaign-test-sms-toggle"
          title={hasSmsChannel ? "Send a test SMS" : "No SMS channel on this campaign"}
          className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 text-white text-[11px] font-semibold px-3 py-1 shadow-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MessageSquare className="w-3 h-3" /> Send test SMS
        </button>
      </div>

      {preflightOpen && (
        <div
          className="pointer-events-auto bg-white rounded-xl border border-slate-200 shadow-xl p-4 w-[360px] flex flex-col gap-3"
          data-testid="admin-campaign-preflight-popover"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">
                Pre-flight check
              </div>
              <div className="text-xs text-slate-500 leading-snug mt-1">
                Sends one test email and one test SMS, then reports per-channel pass/fail.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPreflightOpen(false)}
              className="rounded-full w-7 h-7 inline-flex items-center justify-center text-slate-500 hover:bg-slate-100"
              data-testid="admin-campaign-preflight-close"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          <Field label="Test email">
            <input
              type="email"
              value={pfEmail}
              onChange={(e) => setPfEmail(e.target.value)}
              placeholder="you@nodera.health"
              data-testid="admin-campaign-preflight-email"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Test phone (E.164 or US/CA local)">
            <input
              type="tel"
              value={pfPhone}
              onChange={(e) => setPfPhone(e.target.value)}
              placeholder="+1 415 555 0100"
              data-testid="admin-campaign-preflight-phone"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
            />
          </Field>
          <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
            <input
              type="checkbox"
              checked={pfSave}
              onChange={(e) => setPfSave(e.target.checked)}
              data-testid="admin-campaign-preflight-save-defaults"
              className="rounded border-slate-300"
            />
            Save these as my defaults
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPreflightOpen(false)}
              className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
              data-testid="admin-campaign-preflight-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={runPreflight}
              disabled={pfRunning}
              data-testid="admin-campaign-preflight-run"
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
            >
              <Rocket className="w-3 h-3" /> {pfRunning ? "Running…" : "Run check"}
            </button>
          </div>
          {pfResult && (
            <div className="border-t border-slate-100 pt-3 flex flex-col gap-2" data-testid="admin-campaign-preflight-results">
              <PreflightRow label="Email" result={pfResult.email} testId="preflight-result-email" />
              <PreflightRow label="SMS" result={pfResult.sms} testId="preflight-result-sms" />
            </div>
          )}
          <div className="text-[10px] text-slate-500 leading-snug">
            Tags every send as TEST PREVIEW — no campaign metrics are recorded.
          </div>
        </div>
      )}

      {emailOpen && (
        <div
          className="pointer-events-auto bg-white rounded-xl border border-slate-200 shadow-xl p-3 w-80 flex flex-col gap-2"
          data-testid="admin-campaign-test-email-popover"
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            Recipient (defaults to your admin email)
          </div>
          <input
            type="email"
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
            placeholder="you@nodera.health"
            data-testid="admin-campaign-test-email-input"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEmailOpen(false)}
              className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
              data-testid="admin-campaign-test-email-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={sendEmail}
              disabled={sendingEmail}
              data-testid="admin-campaign-test-email-send"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
            >
              {sendingEmail ? "Sending…" : "Send"}
            </button>
          </div>
          <div className="text-[10px] text-slate-500 leading-snug">
            Renders the campaign's email channel as a one-off TEST PREVIEW. No campaign metrics are recorded.
          </div>
        </div>
      )}
      {smsOpen && (
        <div
          className="pointer-events-auto bg-white rounded-xl border border-slate-200 shadow-xl p-3 w-80 flex flex-col gap-2"
          data-testid="admin-campaign-test-sms-popover"
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            Recipient phone (E.164 or US/CA local)
          </div>
          <input
            type="tel"
            value={smsRecipient}
            onChange={(e) => setSmsRecipient(e.target.value)}
            placeholder="+1 415 555 0100"
            data-testid="admin-campaign-test-sms-input"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setSmsOpen(false)}
              className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
              data-testid="admin-campaign-test-sms-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={sendSms}
              disabled={sendingSms || !smsRecipient.trim()}
              data-testid="admin-campaign-test-sms-send"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
            >
              {sendingSms ? "Sending…" : "Send"}
            </button>
          </div>
          <div className="text-[10px] text-slate-500 leading-snug">
            Prefixed with [TEST PREVIEW] · "Reply STOP to opt out." auto-appended for compliance. No campaign metrics are recorded.
          </div>
        </div>
      )}
    </div>
  );
}

function PreflightRow({ label, result, testId }) {
  if (!result) return null;
  const { ok, applicable, reason, error, to } = result;
  if (!applicable) {
    return (
      <div
        className="flex items-start gap-2 text-xs"
        data-testid={testId}
        data-status="skipped"
      >
        <span className="inline-flex w-5 h-5 rounded-full bg-slate-200 items-center justify-center text-slate-600 text-[10px] font-bold">—</span>
        <div className="flex-1">
          <div className="font-semibold text-slate-700">{label}</div>
          <div className="text-slate-500">{reason || "Not applicable."}</div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex items-start gap-2 text-xs"
      data-testid={testId}
      data-status={ok ? "pass" : "fail"}
    >
      <span
        className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-white ${
          ok ? "bg-emerald-600" : "bg-rose-600"
        }`}
      >
        {ok ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900">
          {label}{" "}
          <span className="font-normal text-slate-500">
            {ok ? `→ ${to}` : "failed"}
          </span>
        </div>
        {!ok && (
          <div className="text-rose-600 break-words" data-testid={`${testId}-error`}>
            {error || reason || "Unknown error."}
          </div>
        )}
      </div>
    </div>
  );
}
